import AutocheckinController from "./autocheckin_controller"
import { Timestampable } from "./concerns/timestampable"
// Vimeo included in html layout

// vimeo player doc: https://developer.vimeo.com/player/sdk
export default class extends AutocheckinController {
  static targets = [ 'iframeContainer' ]

  static values = {
    initialTimestamp: String,
    inline: { type: Boolean, default: false }
  }

  static outlets = [ "post-open-session" ]

  // contador de cantidad de disparos del evento "timeupdate"
  // timeupdate se dispara cada aprox 250ms
  timeUpdatesCount = 0

  // solo hacer checkin después de 2min seguidos de reproducción
  //  si hacen "seeked" no se resetea la cuenta.
  checkinWaitTimeUpdatesCount = (2*60*1000)/250
  keyboardSeekSeconds = 5

  initialize(){
    super.initialize()
    Timestampable(this)
  }

  connect(){
    this.connected = true
    this.hasPlayed = false
    this.connectPlayer()
  }

  connectPlayer(){
    if (!this.connected || this.endedHandler) return

    try { Vimeo } catch (e) {
      this.log("Vimeo is not defined yet")
      this.connectionTimeout = setTimeout(() => this.connectPlayer(), 300)
      return
    }

    this.adjustContainerRatio()
    this.restorePosition()

    this.endedHandler = () => {
      if(this.shouldCheckin()){
        this.inlineValue ? this.silentCheckin() : this.checkinAndRedirect()
      } else {
        if (this.hasPostOpenSessionOutlet){
          this.postOpenSessionOutlet.destroySession()
        }
      }
    }
    this.playHandler = () => {
      this.hasPlayed = true
      if (this.hasPostOpenSessionOutlet) this.postOpenSessionOutlet.activate()
    }
    this.pauseHandler = () => {
      if (this.hasPlayed && this.hasPostOpenSessionOutlet) {
        this.postOpenSessionOutlet.updateServerWithCurrentPosition()
      }
    }
    this.timeupdateHandler = (event) => {
      if (!this.hasPlayed) return
      this.timeUpdatesCount++
      if (this.hasPostOpenSessionOutlet){
        this.postOpenSessionOutlet.tickUpdateSession(this.timeUpdatesCount , {position: event.seconds})
      }
      if(this.shouldSilentCheckin(event)){
        this.silentCheckin()
      }
    }
    this.player().on("play", this.playHandler)
    this.player().on("pause", this.pauseHandler)
    this.player().on("ended", this.endedHandler)
    this.player().on("timeupdate", this.timeupdateHandler)
  }

  disconnect(){
    this.connected = false
    clearTimeout(this.connectionTimeout)
    if (this.vimeoPlayer && this.endedHandler) {
      this.vimeoPlayer.off("play", this.playHandler)
      this.vimeoPlayer.off("pause", this.pauseHandler)
      this.vimeoPlayer.off("ended", this.endedHandler)
      this.vimeoPlayer.off("timeupdate", this.timeupdateHandler)
    }
    this.playHandler = null
    this.pauseHandler = null
    this.endedHandler = null
    this.timeupdateHandler = null
  }

  aboveCheckinPercentageThreashold(event){
    let per = null;
    if(event.percent === undefined){
      per = event.data.data.percent;
    } else {
      per = event.percent;
    }
    return (per*100)>this.data.get("percentageForAutoCheckin")
  }

  shouldSilentCheckin(event){
    return this.shouldCheckin(event) && this.aboveCheckinPercentageThreashold(event) && (this.timeUpdatesCount > this.checkinWaitTimeUpdatesCount)
  }

  togglePlay(keyEvent){
    this.log("toggle play")
    if (this.inlineValue || !this.endedHandler || this.keyboardEventTargetIsEditable(keyEvent)) {
      return
    }

    keyEvent.preventDefault()
    this.player().getPaused().then((paused) => {
      if(paused){
        this.player().play()
      } else {
        this.player().pause()
      }
    })
  }

  skipBackward(keyEvent){
    this.skipBySeconds(keyEvent, -this.keyboardSeekSeconds)
  }

  skipForward(keyEvent){
    this.skipBySeconds(keyEvent, this.keyboardSeekSeconds)
  }

  skipBySeconds(keyEvent, seconds){
    this.log("skip seconds", seconds)
    if (this.inlineValue || !this.endedHandler || this.keyboardEventTargetIsEditable(keyEvent)) {
      return
    }

    keyEvent.preventDefault()
    const player = this.player()

    Promise.all([player.getCurrentTime(), player.getDuration()]).then(([currentTime, duration]) => {
      this.setPosition(this.clamp(currentTime + seconds, 0, duration))
    })
  }

  keyboardEventTargetIsEditable(event) {
    if (!event.target) {
      return false
    }

    return ["INPUT", "TEXTAREA", "SELECT"].includes(event.target.tagName) || event.target.isContentEditable
  }

  clamp(number, min, max) {
    return Math.min(Math.max(number, min), max)
  }

  restorePosition(){
    if (this.hasInitialTimestampValue && this.isTimestamp(this.initialTimestampValue)){
      this.setPosition(this.timestampToSeconds(this.initialTimestampValue))
    }
  }

  setPosition(seconds){
    this.log("setting position: ", seconds)
    this.player().setCurrentTime(seconds).then((seconds)=>{
      // `seconds` indicates the actual time that the player seeks to
    }).catch((error) => {
      this.error(error.name)
      switch (error.name) {
        case 'RangeError':
          // The time is less than 0 or greater than the video's duration
          break;
        default:
          // Some other error occurred
          break;
      }
    })
  }

  iframe(){
    return this.iframeContainerTarget.querySelector("iframe")
  }
  player(){
    this.vimeoPlayer ||= new Vimeo.Player(this.iframe())
    return this.vimeoPlayer
  }

  async isVertical(){
    let dimensions = await Promise.all([this.player().getVideoWidth(), this.player().getVideoHeight()])
    let width = dimensions[0];
    let height = dimensions[1];
    return width < height
  }

  // si el video es vertical, ajustar el ratio del contenedor
  async adjustContainerRatio(){
    let isVertical = await this.isVertical()
    if (isVertical){
      this.element.classList.remove("ratio-16x9")
      this.element.classList.add("ratio-1x1")
    }
  }

}
